import { useEffect, useState, type FormEvent } from 'react'

import { api, ErrorApi, type EntradaOffgrid, type ItemOffgrid, type ModoOffgrid, type OpcionesOffgrid, type ResultadoOffgrid as Resultado } from '../api'
import { leerNumero as aNumero } from '../formato'
import { BarraGuardar } from './BarraGuardar'
import { DescuentoCliente } from './DescuentoCliente'
import { CampoUtilidad, leerUtilidad, porcentaje } from './CampoUtilidad'
import { CamposManuales } from './CamposManuales'
import { FilasEquipo } from './FilasEquipo'
import { PartidasQuitadas } from './PartidasQuitadas'
import { ResultadoOffgrid } from './ResultadoOffgrid'

const MODOS: { modo: ModoOffgrid; etiqueta: string }[] = [
  { modo: 'respaldo', etiqueta: 'Solo respaldo (sin paneles)' },
  { modo: 'paneles', etiqueta: 'Con paneles (cantidades a mano)' },
  { modo: 'kit', etiqueta: 'Kit del catálogo' },
]

interface Props {
  clienteId: string
  /** La entrada de una version anterior, para partir de ella. */
  base?: EntradaOffgrid
  alGuardar: (cotizacionId: string) => void
  alCancelar: () => void
  /** El asesor cambio "Sistema" a On-grid o Hibrido: vuelve al formulario con consumo. */
  alElegirEstandar: () => void
}

const texto = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v))

export function NuevaCotizacionOffgrid({ clienteId, base, alGuardar, alCancelar, alElegirEstandar }: Props) {
  const [opciones, setOpciones] = useState<OpcionesOffgrid | null>(null)
  const [modo, setModo] = useState<ModoOffgrid>(base?.modo ?? 'respaldo')
  const [kit, setKit] = useState(base?.kit ?? '')
  const [panel, setPanel] = useState<ItemOffgrid>(base?.paneles ?? { codigo: '', cantidad: 10 })
  const [inversores, setInversores] = useState<ItemOffgrid[]>(base?.inversores ?? [])
  const [baterias, setBaterias] = useState<ItemOffgrid[]>(base?.baterias ?? [])
  const [accesorios, setAccesorios] = useState<ItemOffgrid[]>(base?.accesorios ?? [])
  const [materiales, setMateriales] = useState(texto(base?.materiales_usd))
  const [instalacion, setInstalacion] = useState(texto(base?.instalacion_usd))
  const [descuento, setDescuento] = useState(base ? String(base.descuento_pct * 100) : '0')
  const [nota, setNota] = useState('')
  const [excluir, setExcluir] = useState<string[]>(base?.excluir ?? [])
  const [costos, setCostos] = useState<Record<string, number>>(base?.costos ?? {})
  // Utilidad de esta cotizacion (%). Parte de la de siempre (45 %); se puede cambiar.
  const [utilidad, setUtilidad] = useState(base?.margen_pct != null ? String(porcentaje(base.margen_pct)) : '')

  const [calculada, setCalculada] = useState<{ entrada: EntradaOffgrid; resultado: Resultado } | null>(null)
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState<'' | 'calculando' | 'guardando'>('')

  useEffect(() => {
    api
      .opcionesOffgrid()
      .then((o) => {
        setOpciones(o)
        setUtilidad((u) => u || (o.margen_por_defecto === undefined ? '' : String(porcentaje(o.margen_por_defecto))))
        // Sin version anterior, se parte de la primera opcion de cada lista: el asesor cambia lo que haga falta.
        setInversores((v) => (v.length ? v : [{ codigo: o.inversores[0]!.codigo, cantidad: 1 }]))
        setBaterias((v) => (v.length ? v : [{ codigo: o.baterias[0]!.codigo, cantidad: 1 }]))
        setPanel((p) => (p.codigo ? p : { codigo: o.paneles[0]!.codigo, cantidad: p.cantidad }))
        setKit((k) => k || o.kits[0]?.codigo || '')
      })
      .catch((err) => setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar.'))
  }, [])

  const cambia = <T,>(fijar: (v: T) => void) => (v: T) => {
    fijar(v)
    setCalculada(null)
  }

  // Lo que se retoca a mano (costos de la tabla y campos de materiales/instalacion), junto
  // para pasar el valor NUEVO a `correr` sin esperar al estado de React.
  const manuales = { costos, materiales, instalacion }
  type Manuales = typeof manuales

  const armarEntrada = (quitadas: string[], retoques: Manuales): EntradaOffgrid | string => {
    // Una partida quitada no puede llevar valor manual: se ignora el campo.
    const sinInstalacion = quitadas.includes('instalacion') || quitadas.includes('instalacion_manual')
    const m = quitadas.includes('materiales_electricos') ? null : aNumero(retoques.materiales)
    const i = sinInstalacion ? null : aNumero(retoques.instalacion)
    const d = aNumero(descuento) ?? 0
    if (Number.isNaN(m) || (m !== null && m < 0)) return 'Los materiales eléctricos tienen que ser un valor en USD, por ejemplo 350.'
    if (Number.isNaN(i) || (i !== null && i < 0)) return 'La instalación tiene que ser un valor en USD, por ejemplo 400.'
    if (Number.isNaN(d) || d < 0) return 'El descuento no es un número válido.'
    if (opciones && d / 100 > opciones.descuento_maximo_pct) return `El descuento máximo permitido es ${opciones.descuento_maximo_pct * 100} %.`
    const cantidades = [...inversores, ...baterias, ...accesorios, ...(modo === 'paneles' ? [panel] : [])]
    if (modo !== 'kit' && cantidades.some((f) => !(f.cantidad >= 1))) return 'La cantidad de cada equipo tiene que ser al menos 1.'
    const margen = leerUtilidad(utilidad, opciones?.margen_por_defecto)
    if (typeof margen === 'string') return margen
    if (modo === 'kit' && !kit) return 'Elige el kit.'
    if (modo === 'respaldo' && i === null && !sinInstalacion) return 'En el modo respaldo escribe la instalación: la fija el técnico.'
    return {
      modo,
      inversores: modo === 'kit' ? [] : inversores,
      baterias: modo === 'kit' ? [] : baterias,
      accesorios: modo === 'kit' ? [] : accesorios,
      paneles: modo === 'paneles' ? panel : null,
      kit: modo === 'kit' ? kit : null,
      materiales_usd: m,
      instalacion_usd: i,
      descuento_pct: d / 100,
      excluir: quitadas,
      margen_pct: margen,
      costos: retoques.costos,
    }
  }

  const calcular = (e: FormEvent) => {
    e.preventDefault()
    void correr(excluir, manuales)
  }

  // Quitar o devolver una partida, o cambiar un costo, recalcula al instante.
  const cambiarQuitadas = (nuevas: string[]) => {
    setExcluir(nuevas)
    void correr(nuevas, manuales)
  }
  const quitar = (codigo: string) => cambiarQuitadas([...excluir, codigo])
  const restaurar = (codigo: string) => cambiarQuitadas(excluir.filter((c) => c !== codigo))
  const cambiarManuales = (nuevos: Manuales) => {
    setCostos(nuevos.costos)
    setMateriales(nuevos.materiales)
    setInstalacion(nuevos.instalacion)
    void correr(excluir, nuevos)
  }
  const edicionCostos = {
    cambiados: costos,
    alCambiar: (codigo: string, nuevo: number) => {
      // Si esa partida esta fijada en su campo (materiales o instalacion), la celda cambia ESE campo.
      if (codigo === 'materiales_electricos' && materiales.trim() !== '') cambiarManuales({ ...manuales, materiales: String(nuevo) })
      else if (codigo === 'instalacion_manual' && instalacion.trim() !== '') cambiarManuales({ ...manuales, instalacion: String(nuevo) })
      else cambiarManuales({ ...manuales, costos: { ...costos, [codigo]: nuevo } })
    },
    alRestablecer: (codigo: string) =>
      cambiarManuales({ ...manuales, costos: Object.fromEntries(Object.entries(costos).filter(([c]) => c !== codigo)) }),
  }

  const correr = async (quitadas: string[], retoques: Manuales) => {
    const entrada = armarEntrada(quitadas, retoques)
    if (typeof entrada === 'string') {
      setError(entrada)
      return
    }
    setError('')
    setOcupado('calculando')
    try {
      const { resultado } = await api.calcularOffgrid(entrada)
      setCalculada({ entrada, resultado })
    } catch (err) {
      setCalculada(null)
      setError(err instanceof ErrorApi ? err.message : 'No se pudo calcular.')
    } finally {
      setOcupado('')
    }
  }

  const guardar = async () => {
    if (!calculada) return
    setOcupado('guardando')
    try {
      const { cotizacion } = await api.guardarOffgrid(clienteId, calculada.entrada, nota)
      alGuardar(cotizacion.id)
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.')
      setOcupado('')
    }
  }

  return (
    <section>
      <div className="encabezado">
        <div>
          <h1>{base ? 'Nueva versión off-grid' : 'Nueva cotización off-grid'}</h1>
          <p className="suave">Sin consumo ni análisis financiero: solo equipos y precio. Nada se envía al cliente.</p>
        </div>
        <button className="secundario" onClick={alCancelar}>
          Cancelar
        </button>
      </div>

      <form className="tarjeta formulario-cotizacion" onSubmit={calcular}>
        <label>
          Sistema
          <select value="offgrid" onChange={() => alElegirEstandar()}>
            <option value="no">On-grid</option>
            <option value="si">Híbrido con batería</option>
            <option value="offgrid">Off-grid</option>
          </select>
        </label>

        <div className="filtros">
          {MODOS.map((m) => (
            <button type="button" key={m.modo} className={modo === m.modo ? 'chip activo' : 'chip'} onClick={() => cambia(setModo)(m.modo)}>
              {m.etiqueta}
            </button>
          ))}
        </div>

        {modo === 'kit' && (
          <label>
            Kit
            <select value={kit} onChange={(e) => cambia(setKit)(e.target.value)}>
              {(opciones?.kits ?? []).map((k) => (
                <option key={k.codigo} value={k.codigo}>
                  {k.nombre} · {k.potencia_kwp} kWp · {k.capacidad_bateria_kwh} kWh
                </option>
              ))}
            </select>
          </label>
        )}

        {modo === 'paneles' && opciones && (
          <FilasEquipo titulo="Paneles" opciones={opciones.paneles} filas={[panel]} minimo={1} textoAgregar="" alCambiar={(f) => cambia(setPanel)(f[0]!)} />
        )}

        {modo !== 'kit' && opciones && (
          <>
            <FilasEquipo titulo="Inversor" opciones={opciones.inversores} filas={inversores} minimo={1} textoAgregar="Agregar otro inversor" alCambiar={cambia(setInversores)} />
            <FilasEquipo titulo="Batería" opciones={opciones.baterias} filas={baterias} minimo={1} textoAgregar="Agregar otra batería" alCambiar={cambia(setBaterias)} />
            <FilasEquipo titulo="Accesorios" opciones={opciones.accesorios} filas={accesorios} textoAgregar="Agregar accesorio (paralelismo)" alCambiar={cambia(setAccesorios)} />
          </>
        )}

        <CamposManuales
          materiales={materiales}
          alCambiarMateriales={cambia(setMateriales)}
          instalacion={instalacion}
          alCambiarInstalacion={cambia(setInstalacion)}
          materialesQuitados={excluir.includes('materiales_electricos')}
          instalacionQuitada={excluir.includes('instalacion') || excluir.includes('instalacion_manual')}
          placeholderInstalacion={modo === 'respaldo' ? 'Obligatoria: la fija el técnico' : 'Vacío = por kWp, como siempre'}
        />
        <CampoUtilidad valor={utilidad} alCambiar={cambia(setUtilidad)} porDefecto={opciones?.margen_por_defecto} />
        <DescuentoCliente valor={descuento} alCambiar={cambia(setDescuento)} maximo={opciones?.descuento_maximo_pct} />

        <div className="accion-formulario">
          <button className="primario" disabled={ocupado !== '' || !opciones}>
            {ocupado === 'calculando' ? 'Calculando…' : calculada ? 'Recalcular' : 'Calcular'}
          </button>
        </div>
      </form>

      {error && <p className="error">{error}</p>}

      {calculada && (
        <>
          <PartidasQuitadas excluir={excluir} alRestaurar={restaurar} />
          <ResultadoOffgrid resultado={calculada.resultado} alQuitar={quitar} costos={edicionCostos} />
          <BarraGuardar nota={nota} alCambiarNota={setNota} alGuardar={guardar} guardando={ocupado === 'guardando'} deshabilitado={ocupado !== ''} />
        </>
      )}
    </section>
  )
}
